import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError, loadError } from "@/lib/api";
import { LoadFailed } from "@/components/LoadState";
import { Crosshair } from "@/components/Crosshair";
import { toast } from "@/lib/toast";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  SlidersHorizontal, Save, Download, Layers, Receipt, Tags, Medal, ShieldCheck,
  FileText, FlaskConical, Lock, Clock, AlertTriangle, X, RotateCcw, CalendarClock,
} from "lucide-react";

/* ---------- tiny field primitives ---------- */
function MetaHint({ meta, path }) {
  const m = meta?.[path];
  if (!m) return null;
  const when = m.at ? new Date(m.at).toLocaleDateString() : "";
  return (
    <span className="block mt-0.5 font-mono text-[9px] uppercase tracking-wide text-muted-foreground" title={m.reason || ""}>
      {m.by_username || "admin"} · {when}
    </span>
  );
}

function ReadRow({ label, value, testId, muted }) {
  return (
    <div className="flex items-center justify-between gap-2 font-mono text-xs">
      <span className="uppercase text-muted-foreground">{label}</span>
      <span className={muted ? "text-muted-foreground" : "text-foreground"} data-testid={testId}>{value}</span>
    </div>
  );
}

function NumField({ label, value, onChange, prefix, suffix, step = "1", min, max, testId, meta, path, hint }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">{label}</label>
      <div className="relative mt-1">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-sm">{prefix}</span>}
        <input
          type="number" step={step} min={min} max={max} data-testid={testId}
          value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          className={`w-full bg-input border border-sniper focus:border-olive outline-none ${prefix ? "pl-7" : "pl-3"} pr-10 py-2 text-foreground font-mono text-sm text-right transition-colors`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-[11px] uppercase">{suffix}</span>}
      </div>
      {hint && <span className="block mt-0.5 font-mono text-[9px] uppercase tracking-wide text-muted-foreground/70">{hint}</span>}
      <MetaHint meta={meta} path={path} />
    </div>
  );
}

function TextField({ label, value, onChange, testId, meta, path }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">{label}</label>
      <input
        type="text" data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-sm transition-colors"
      />
      <MetaHint meta={meta} path={path} />
    </div>
  );
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <button
      type="button" onClick={() => onChange(!checked)} data-testid={testId}
      className="flex items-center justify-between gap-3 w-full border border-sniper bg-input px-3 py-2 transition-colors hover:border-olive"
    >
      <span className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground text-left">{label}</span>
      <span className={`inline-flex w-9 h-5 rounded-full items-center px-0.5 transition-colors ${checked ? "bg-olive" : "bg-sniper"}`}>
        <span className={`w-4 h-4 rounded-full bg-white transition-transform ${checked ? "translate-x-4" : ""}`} />
      </span>
    </button>
  );
}

function Section({ icon: Icon, title, desc, children, testId }) {
  return (
    <section className="border border-sniper bg-surface p-5 mt-4" data-testid={testId}>
      <h3 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2">
        <Icon className="w-4 h-4 text-olive-bright" /> {title}
      </h3>
      {desc && <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1 leading-relaxed">{desc}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

/* ---------- diff builder ---------- */
function buildChanges(orig, d) {
  const changes = {};
  const changed = [];

  // Membership tiers + individual commission % are owned by the Tier Permissions panel
  // (single writer). They are NOT editable here, so they are intentionally never diffed or
  // sent — closing the Gap-D double-writer on both the UI and the wire.

  // dealer fees
  const dealerDiff = {};
  Object.entries(d.fees.commission_dealer).forEach(([k, v]) => {
    if (Number(v) !== Number(orig.fees.commission_dealer[k])) { dealerDiff[k] = Number(v); changed.push(`dealer_fee.${k}`); }
  });
  if (Object.keys(dealerDiff).length) changes.commission_dealer = dealerDiff;

  const scalar = (val, ov, key, isInt) => {
    if (Number(val) !== Number(ov)) { changes[key] = isInt ? parseInt(val, 10) : Number(val); changed.push(key); }
  };
  scalar(d.fees.platform_min_fee, orig.fees.platform_min_fee, "platform_min_fee");
  scalar(d.fees.buyer_fee_cents, orig.fees.buyer_fee_cents, "buyer_fee_cents", true);
  scalar(d.a_la_carte.post_fee, orig.a_la_carte.post_fee, "post_fee");
  scalar(d.a_la_carte.bump_fee, orig.a_la_carte.bump_fee, "bump_fee");
  if (JSON.stringify(d.a_la_carte.credit_packs) !== JSON.stringify(orig.a_la_carte.credit_packs)) {
    changes.credit_packs = d.a_la_carte.credit_packs.map(Number); changed.push("credit_packs");
  }

  if (!!d.verification.identity_required_to_list !== !!orig.verification.identity_required_to_list) { changes.identity_required_to_list = !!d.verification.identity_required_to_list; changed.push("identity_required_to_list"); }
  scalar(d.verification.identity_max_attempts, orig.verification.identity_max_attempts, "identity_max_attempts", true);
  if (!!d.verification.dealer_verification_required !== !!orig.verification.dealer_verification_required) { changes.dealer_verification_required = !!d.verification.dealer_verification_required; changed.push("dealer_verification_required"); }
  if (JSON.stringify(d.verification.ffl_expiry_warning_days) !== JSON.stringify(orig.verification.ffl_expiry_warning_days)) { changes.ffl_expiry_warning_days = d.verification.ffl_expiry_warning_days.map(Number); changed.push("ffl_expiry_warning_days"); }

  scalar(d.listing_rules.max_listing_price, orig.listing_rules.max_listing_price, "max_listing_price", true);
  scalar(d.listing_rules.title_min, orig.listing_rules.title_min, "title_min", true);
  scalar(d.listing_rules.title_max, orig.listing_rules.title_max, "title_max", true);
  scalar(d.listing_rules.username_cooldown_days, orig.listing_rules.username_cooldown_days, "username_cooldown_days", true);
  scalar(d.listing_rules.username_reserve_days, orig.listing_rules.username_reserve_days, "username_reserve_days", true);
  if (!!d.listing_rules.trust_alerts_public !== !!orig.listing_rules.trust_alerts_public) { changes.trust_alerts_public = !!d.listing_rules.trust_alerts_public; changed.push("trust_alerts_public"); }
  scalar(d.beta.beta_seat_cap, orig.beta.beta_seat_cap, "beta_seat_cap", true);

  const oEm = JSON.stringify((orig.internal?.internal_exempt_emails || []));
  const dEm = JSON.stringify((d.internal?.internal_exempt_emails || []).map((e) => String(e).trim().toLowerCase()).filter(Boolean));
  if (oEm !== dEm) { changes.internal_exempt_emails = JSON.parse(dEm); changed.push("internal_exempt_emails"); }

  return { changes, changed };
}

export default function AdminPlatformConfig() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [denied, setDenied] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(false);
  const [reason, setReason] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");

  const load = async () => {
    setErr(null);
    try {
      const { data } = await api.get("/admin/platform-config", { timeout: 10000 });
      setCfg(data);
      setDraft(JSON.parse(JSON.stringify(data)));
      setDenied(false);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
      else setErr(loadError(e, "Couldn't load platform configuration."));
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const { changes, changed } = useMemo(() => (cfg && draft ? buildChanges(cfg, draft) : { changes: {}, changed: [] }), [cfg, draft]);
  const dirty = changed.length > 0;

  const setPath = (section, key, val) => setDraft((p) => ({ ...p, [section]: { ...p[section], [key]: val } }));

  const doSave = async () => {
    if (reason.trim().length < 20) { toast.error("Reason must be at least 20 characters."); return; }
    setBusy(true);
    try {
      const payload = { reason: reason.trim(), changes };
      if (effectiveFrom) payload.effective_from = new Date(effectiveFrom).toISOString();
      const { data } = await api.put("/admin/platform-config", payload);
      (data.warnings || []).forEach((w) => toast.warning(w));
      if (data.scheduled) toast.success(`Change scheduled for ${new Date(data.effective_from).toLocaleString()}.`);
      else toast.success("Platform config saved. The app reads these values live — no redeploy.");
      setModal(false); setReason(""); setEffectiveFrom("");
      await load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const exportJson = async () => {
    try {
      const { data } = await api.get("/admin/platform-config/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `opticswap-platform-config-${new Date().toISOString().slice(0, 10)}.json`;
      a.click(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const cancelScheduled = async (id) => {
    try { await api.post(`/admin/platform-config/scheduled/${id}/cancel`); toast.success("Scheduled change cancelled."); await load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  if (denied) return (
    <div className="max-w-[720px] mx-auto px-4 py-16 text-foreground" data-testid="platform-config-denied">
      <div className="border border-blaze/50 bg-blaze/5 p-8 flex items-start gap-3">
        <Lock className="w-7 h-7 text-blaze shrink-0" />
        <div>
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">Super admin only</h2>
          <p className="mt-2 text-sm font-body text-secondary-foreground">Platform Configuration controls every business number and is restricted to super administrators.</p>
        </div>
      </div>
    </div>
  );

  if (err) return (
    <div className="max-w-[1000px] mx-auto px-4 py-6 text-foreground" data-testid="platform-config-page">
      <LoadFailed reason={err} onRetry={load} testid="platform-config-load-failed" />
    </div>
  );

  if (!draft) return <div className="max-w-[1000px] mx-auto px-4 py-6 font-mono uppercase text-sm text-muted-foreground">Loading…</div>;

  const meta = cfg.meta || {};
  const sr = cfg.stripe_reference || {};

  return (
    <div className="text-foreground pb-40" data-testid="admin-platform-config">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-4xl font-bold uppercase tracking-tight flex items-center gap-2">
            <SlidersHorizontal className="w-7 h-7 text-olive-bright" /> Platform Configuration
          </h2>
          <p className="text-sm font-body text-secondary-foreground mt-2 max-w-2xl">
            The single source of truth for every business number. The pricing page, listing flow, checkout and admin dropdowns all read from here — a save takes effect with no redeploy. Every change needs a reason (20+ chars) and is audit-logged.
          </p>
        </div>
        <button onClick={exportJson} data-testid="config-export"
          className="inline-flex items-center gap-2 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-xs px-4 py-2.5 transition-colors">
          <Download className="w-4 h-4" /> Export JSON
        </button>
      </div>
      <Crosshair className="my-5" />

      {/* scheduled changes */}
      {(cfg.scheduled || []).length > 0 && (
        <div className="border border-volt/40 bg-volt/5 p-4 mb-2" data-testid="scheduled-changes">
          <p className="font-mono text-[11px] uppercase tracking-wide text-volt flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Scheduled changes</p>
          <ul className="mt-2 space-y-1">
            {cfg.scheduled.map((s) => (
              <li key={s.change_id} className="flex items-center justify-between gap-3 text-sm font-mono text-secondary-foreground">
                <span>{new Date(s.effective_from).toLocaleString()} — {Object.keys(s.flat_set || {}).join(", ").slice(0, 80)}</span>
                <button onClick={() => cancelScheduled(s.change_id)} data-testid={`cancel-scheduled-${s.change_id}`} className="text-muted-foreground hover:text-blaze"><X className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 1. MEMBERSHIP TIERS — READ-ONLY (owned by the Tier Permissions panel) */}
      <Section icon={Layers} title="1 · Membership Tiers" testId="section-tiers"
        desc="Read-only here. Tier prices, allowances, saved-search caps and the platform commission % have ONE writer — the Tier Permissions panel — which projects them into config. Edit them there so a live rate can never drift.">
        <Link to="/admin/tier-permissions" data-testid="tiers-panel-link"
          className="flex items-center gap-2 border border-olive/50 bg-olive/5 px-4 py-3 mb-4 text-olive-bright hover:border-olive transition-colors">
          <Lock className="w-4 h-4 shrink-0" />
          <span className="font-mono text-[11px] uppercase tracking-wide">Edit tiers, prices &amp; commission in Configuration → Tier Permissions →</span>
        </Link>
        <div className="grid md:grid-cols-3 gap-4">
          {draft.membership_tiers.map((t) => (
            <div key={t.id} className="border border-sniper p-4 space-y-2" data-testid={`tier-${t.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-heading text-lg uppercase tracking-tight">{t.label || t.id}</span>
                <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 border ${t.active ? "border-olive/40 text-olive-bright" : "border-sniper text-muted-foreground"}`}>{t.active ? "Active" : "Off"}</span>
              </div>
              <ReadRow label="Monthly" value={`$${((t.monthly_price_cents || 0) / 100).toFixed(2)}`} testId={`tier-${t.id}-monthly-ro`} />
              <ReadRow label="Annual" value={`$${((t.annual_price_cents || 0) / 100).toFixed(2)}`} testId={`tier-${t.id}-annual-ro`} />
              <ReadRow label="Commission" value={`${Number(t.platform_fee ?? 0).toFixed(2)}%`} testId={`tier-${t.id}-fee-ro`} />
              <ReadRow label="Free listings/mo" value={t.free_listings ?? 0} testId={`tier-${t.id}-freelist-ro`} />
              <ReadRow label="Bumps/mo" value={t.free_priority_listings ?? 0} testId={`tier-${t.id}-bumps-ro`} />
              <ReadRow label="Saved searches" value={t.saved_filters_mode === "unlimited" ? "Unlimited" : (t.saved_search_limit || 0)} testId={`tier-${t.id}-saved-ro`} />
            </div>
          ))}
        </div>
      </Section>

      {/* 2. TRANSACTION FEES */}
      <Section icon={Receipt} title="2 · Transaction Fees" testId="section-fees"
        desc="Fee base: GROSS SALE PRICE (item price) BEFORE shipping — shipping is never in the fee base. Individual tier commission % is owned by the Tier Permissions panel (read-only above). Dealer fee schedule + the per-sale minimum are edited here.">
        <div className="grid sm:grid-cols-3 gap-3">
          {Object.entries(draft.fees.commission_dealer).map(([k, v]) => (
            <NumField key={k} label={`Dealer · ${k}`} value={v} onChange={(nv) => setDraft((p) => ({ ...p, fees: { ...p.fees, commission_dealer: { ...p.fees.commission_dealer, [k]: nv } } }))} suffix="%" step="0.1" min="0" max="100" testId={`dealer-fee-${k}`} meta={meta} path={`commission_dealer.${k}`} />
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <NumField label="Minimum fee per sale" value={draft.fees.platform_min_fee} onChange={(v) => setPath("fees", "platform_min_fee", v)} prefix="$" step="0.01" min="0" testId="platform-min-fee" meta={meta} path="platform_min_fee" hint="Fee = greater of (rate × price) or this floor" />
          <NumField label="Buyer fee (inactive)" value={draft.fees.buyer_fee_cents} onChange={(v) => setPath("fees", "buyer_fee_cents", v)} suffix="¢" testId="buyer-fee" hint="Field exists; buyer pays item price only today" />
        </div>
      </Section>

      {/* 3. A-LA-CARTE */}
      <Section icon={Tags} title="3 · A-la-carte Pricing" testId="section-alacarte" desc="Per-action prices beyond a tier's allowance, and prepaid credit-pack denominations.">
        <div className="grid sm:grid-cols-2 gap-3">
          <NumField label="Per extra listing" value={draft.a_la_carte.post_fee} onChange={(v) => setPath("a_la_carte", "post_fee", v)} prefix="$" step="0.01" min="0" testId="post-fee" meta={meta} path="post_fee" />
          <NumField label="Per bump" value={draft.a_la_carte.bump_fee} onChange={(v) => setPath("a_la_carte", "bump_fee", v)} prefix="$" step="0.01" min="0" testId="bump-fee" meta={meta} path="bump_fee" />
        </div>
        <div className="mt-3">
          <label className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Credit packs (cents)</label>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            {draft.a_la_carte.credit_packs.map((c, i) => (
              <div key={i} className="relative">
                <input type="number" min="100" step="100" data-testid={`credit-pack-${i}`} value={c}
                  onChange={(e) => setDraft((p) => ({ ...p, a_la_carte: { ...p.a_la_carte, credit_packs: p.a_la_carte.credit_packs.map((x, j) => (j === i ? Number(e.target.value) : x)) } }))}
                  className="w-28 bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-sm text-right" />
                <button onClick={() => setDraft((p) => ({ ...p, a_la_carte: { ...p.a_la_carte, credit_packs: p.a_la_carte.credit_packs.filter((_, j) => j !== i) } }))} className="ml-1 text-muted-foreground hover:text-blaze align-middle"><X className="w-3.5 h-3.5 inline" /></button>
              </div>
            ))}
            <button onClick={() => setDraft((p) => ({ ...p, a_la_carte: { ...p.a_la_carte, credit_packs: [...p.a_la_carte.credit_packs, 1000] } }))} data-testid="credit-pack-add"
              className="border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-[11px] px-3 py-2">+ Pack</button>
          </div>
        </div>
      </Section>

      {/* 4. FOUNDING SEATS */}
      <Section icon={Medal} title="4 · Founding Member Seats" testId="section-founding" desc="A single global pool of 100 defined in code (founding.py) — not editable here. ONE shared sequence for individuals and businesses; seats 1–10 are reserved for super-admin assignment, 11–100 are public. Claimed / remaining / retired are live read-only counters.">
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="border border-sniper bg-obsidian p-3 font-mono text-xs" data-testid="founding-total-seats">
            <div className="uppercase text-muted-foreground">Total seats</div>
            <div className="text-foreground mt-1 text-base">{cfg.founding.total_seats}</div>
          </div>
          <div className="border border-sniper bg-obsidian p-3 font-mono text-xs" data-testid="founding-reserved-range">
            <div className="uppercase text-muted-foreground">Reserved (1..N)</div>
            <div className="text-foreground mt-1 text-base">1–{cfg.founding.reserved}</div>
          </div>
          <div className="border border-sniper bg-obsidian p-3 font-mono text-xs" data-testid="founding-public-seats">
            <div className="uppercase text-muted-foreground">Public / claimable</div>
            <div className="text-foreground mt-1 text-base">{cfg.founding.public_seats}</div>
          </div>
        </div>
        <div className="mt-3 border border-sniper bg-obsidian p-3 font-mono text-xs" data-testid="founding-counter">
          <div className="uppercase text-olive-bright">Live counts (single pool)</div>
          <div className="text-secondary-foreground mt-1">
            claimed {cfg.founding.counters.claimed} · remaining {cfg.founding.counters.remaining} of {cfg.founding.public_seats} · retired {cfg.founding.counters.retired} · reserved assigned {cfg.founding.counters.reserved_assigned}
          </div>
        </div>
      </Section>

      {/* 5. VERIFICATION */}
      <Section icon={ShieldCheck} title="5 · Verification" testId="section-verification" desc="Identity + dealer verification rules and FFL expiry warning windows.">
        <div className="grid sm:grid-cols-2 gap-3">
          <Toggle label="Identity required to sell" checked={draft.verification.identity_required_to_list} onChange={(v) => setPath("verification", "identity_required_to_list", v)} testId="verif-identity-required" />
          <Toggle label="Dealer verify required for badge" checked={draft.verification.dealer_verification_required} onChange={(v) => setPath("verification", "dealer_verification_required", v)} testId="verif-dealer-required" />
          <NumField label="Max verification attempts" value={draft.verification.identity_max_attempts} onChange={(v) => setPath("verification", "identity_max_attempts", v)} testId="verif-max-attempts" meta={meta} path="identity_max_attempts" />
          <TextField label="FFL expiry warnings (days, comma)" value={(draft.verification.ffl_expiry_warning_days || []).join(", ")} onChange={(v) => setPath("verification", "ffl_expiry_warning_days", v.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => !isNaN(n)))} testId="verif-ffl-days" />
        </div>
      </Section>

      {/* 6. LISTING RULES */}
      <Section icon={FileText} title="6 · Listing Rules" testId="section-listing-rules" desc="Guardrails that catch junk listings + username change policy.">
        <div className="grid sm:grid-cols-3 gap-3">
          <NumField label="Max listing price" value={draft.listing_rules.max_listing_price} onChange={(v) => setPath("listing_rules", "max_listing_price", v)} prefix="$" testId="rule-max-price" meta={meta} path="max_listing_price" />
          <NumField label="Title min length" value={draft.listing_rules.title_min} onChange={(v) => setPath("listing_rules", "title_min", v)} testId="rule-title-min" meta={meta} path="title_min" />
          <NumField label="Title max length" value={draft.listing_rules.title_max} onChange={(v) => setPath("listing_rules", "title_max", v)} testId="rule-title-max" meta={meta} path="title_max" />
          <NumField label="Username cooldown (days)" value={draft.listing_rules.username_cooldown_days} onChange={(v) => setPath("listing_rules", "username_cooldown_days", v)} testId="rule-username-cooldown" meta={meta} path="username_cooldown_days" />
          <NumField label="Old username hold (days)" value={draft.listing_rules.username_reserve_days} onChange={(v) => setPath("listing_rules", "username_reserve_days", v)} testId="rule-username-reserve" meta={meta} path="username_reserve_days" />
        </div>
        <div className="mt-3">
          <Toggle label="Public trust alerts (show auto-flag to buyers/sellers)" checked={draft.listing_rules.trust_alerts_public} onChange={(v) => setPath("listing_rules", "trust_alerts_public", v)} testId="rule-trust-alerts-public" />
          <p className="mt-1 font-mono text-[10px] text-muted-foreground">OFF: auto-screening still flags listings and shows them in the admin Listings view, but nothing renders to buyers or sellers. Turn ON only once there's real sold-price data behind the "below market" rule.</p>
        </div>
      </Section>

      {/* 7. BETA */}
      <Section icon={FlaskConical} title="7 · Beta" testId="section-beta" desc="Beta seat cap (editable) + read-only splash/env + seats used.">
        <div className="grid sm:grid-cols-3 gap-3">
          <NumField label="Beta seat cap" value={draft.beta.beta_seat_cap} onChange={(v) => setPath("beta", "beta_seat_cap", v)} testId="beta-cap" meta={meta} path="beta_seat_cap" />
          <div className="border border-sniper bg-obsidian p-3 font-mono text-xs"><div className="uppercase text-muted-foreground">Seats used</div><div className="text-foreground mt-1">{cfg.beta.seats_used}</div></div>
          <div className="border border-sniper bg-obsidian p-3 font-mono text-xs"><div className="uppercase text-muted-foreground">Splash (env)</div><div className="text-foreground mt-1">{cfg.beta.beta_splash_enabled ? "ENABLED" : "OFF"}</div></div>
        </div>
      </Section>

      {/* 8. STRIPE REFERENCE (read-only) */}
      <Section icon={Lock} title="8 · Stripe Cost Reference (read-only)" testId="section-stripe" desc="Stripe's own prices — not ours. Displayed for margin math; edit only in code.">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 font-mono text-xs">
          <div className="border border-sniper bg-obsidian p-3"><div className="uppercase text-muted-foreground">Card %</div><div className="text-foreground mt-1">{sr.card_processing_pct}%</div></div>
          <div className="border border-sniper bg-obsidian p-3"><div className="uppercase text-muted-foreground">Card fixed</div><div className="text-foreground mt-1">{sr.card_fixed_cents}¢</div></div>
          <div className="border border-sniper bg-obsidian p-3"><div className="uppercase text-muted-foreground">Connect payout %</div><div className="text-foreground mt-1">{sr.connect_payout_pct}%</div></div>
          <div className="border border-sniper bg-obsidian p-3"><div className="uppercase text-muted-foreground">Connect fixed</div><div className="text-foreground mt-1">{sr.connect_payout_fixed_cents}¢</div></div>
          <div className="border border-sniper bg-obsidian p-3"><div className="uppercase text-muted-foreground">Identity / verify</div><div className="text-foreground mt-1">{sr.identity_per_verification_cents}¢</div></div>
          <div className="border border-sniper bg-obsidian p-3"><div className="uppercase text-muted-foreground">Last verified</div><div className="text-foreground mt-1">{sr.last_verified}</div></div>
        </div>
      </Section>

      {/* 9. INTERNAL / STAFF EXEMPTIONS */}
      <Section icon={Lock} title="9 · Internal / Staff Exemptions" testId="section-internal"
        desc="EXPLICIT allowlist (never a domain wildcard). These accounts are verified-exempt — never sent to Stripe Identity, never charged, hidden from Pending Verification + reminders, and excluded from ALL public counts & analytics. The exemption is permanent and never expires. Every grant/removal is audited.">
        <div className="space-y-3">
          <div>
            <label className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">Additional exempt emails (one per line)</label>
            <textarea
              data-testid="internal-emails-input"
              rows={5}
              value={(draft.internal?.internal_exempt_emails || []).join("\n")}
              onChange={(e) => setPath("internal", "internal_exempt_emails", e.target.value.split(/[\n,]/).map((s) => s.trim()).filter(Boolean))}
              placeholder="teammate@opticswap.com"
              className="mt-1 w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground font-mono text-sm transition-colors"
            />
            <p className="mt-1 font-mono text-[10px] text-muted-foreground">Adding an email flags any matching account immediately; removing one forces that account back through real ID verification.</p>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Always exempt (hard-coded)</p>
            <div className="flex flex-wrap gap-1.5" data-testid="internal-defaults">
              {(cfg.internal?.internal_defaults || []).map((e) => (
                <span key={e} className="font-mono text-[10px] px-1.5 py-0.5 border border-olive/40 text-olive-bright">{e}</span>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 md:left-[240px] border-t border-sniper bg-obsidian/95 backdrop-blur px-4 py-3 flex items-center justify-between gap-3 z-20" data-testid="config-savebar">
        <span className="font-mono text-[11px] uppercase text-muted-foreground">{dirty ? `${changed.length} change(s) pending` : "No changes"}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => setDraft(JSON.parse(JSON.stringify(cfg)))} disabled={!dirty} data-testid="config-revert"
            className="inline-flex items-center gap-2 border border-sniper hover:border-white disabled:opacity-40 text-secondary-foreground font-mono uppercase text-xs px-4 py-2.5"><RotateCcw className="w-4 h-4" /> Revert</button>
          <button onClick={() => setModal(true)} disabled={!dirty} data-testid="config-save"
            className="inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-40 text-white font-heading font-bold uppercase px-6 py-2.5"><Save className="w-4 h-4" /> Save changes</button>
        </div>
      </div>

      {/* reason + effective-date modal */}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="bg-surface border-sniper text-foreground" data-testid="config-save-modal">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2"><Save className="w-5 h-5 text-olive-bright" /> Confirm config change</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="border border-sniper bg-obsidian p-3 max-h-32 overflow-auto font-mono text-[11px] text-secondary-foreground">
              {changed.map((c) => <div key={c}>· {c}</div>)}
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Reason (min 20 chars) — audit-logged</label>
              <textarea data-testid="config-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                className="w-full mt-1 bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-body text-sm" />
              <span className={`font-mono text-[10px] ${reason.trim().length >= 20 ? "text-olive-bright" : "text-blaze"}`}>{reason.trim().length}/20</span>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> Effective from (optional — future date schedules it)</label>
              <input type="datetime-local" data-testid="config-effective-from" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)}
                className="w-full mt-1 bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-sm" />
              <p className="font-mono text-[9px] uppercase text-muted-foreground mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Existing orders keep the rate recorded at sale time — never re-priced.</p>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setModal(false)} className="border border-sniper text-secondary-foreground font-mono uppercase text-xs px-4 py-2.5">Cancel</button>
            <button onClick={doSave} disabled={busy || reason.trim().length < 20} data-testid="config-confirm"
              className="inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-40 text-white font-heading font-bold uppercase px-6 py-2.5">
              {busy ? "Saving…" : effectiveFrom ? "Schedule" : "Apply now"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
